import { PencilSimpleIcon, UserIcon } from '@phosphor-icons/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetManagers } from '@/api/managers'
import { Button } from '@/components/ui/button'
import { DetailsListItem } from '@/components/ui/details-list'
import { EmailLink } from '@/components/ui/email-link'
import { Flex } from '@/components/ui/flex'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory } from '@/factories'
import { formatDateOnly } from '@/utils/date'

import { AccountEmailsMenu } from '../../shared/account-emails-menu'
import { ActiveStatusBadge } from '../../shared/active-status-badge'
import { DetailsSection } from '../../shared/details-section'
import { DetailsModalSkeleton } from '../../shared/details-skeleton'
import { PersonProfile } from '../../shared/person-profile'
import { ManagerFormModal } from '../manager-form-modal'

interface ManagerDetailsModalProps {
  managerId: number
  onClose: VoidFunction
}

export const ManagerDetailsModal = ({ managerId, onClose }: ManagerDetailsModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const { data, isLoading } = useGetManagers()

  const manager = data?.find((item) => item.id === managerId)
  const isRemoved = !isLoading && !!data && !manager

  // Note: closes once the record is gone from the list
  useEffect(() => {
    if (isRemoved) onClose()
  }, [isRemoved, onClose])

  const handleEdit = () => {
    if (!manager) return
    popupFactory.open(ManagerFormModal, { manager, mode: 'edit' }, { suspendCurrent: true })
  }

  const footer = (
    <>
      <Button use="secondary" onClick={onClose}>
        {t('common:actions.close')}
      </Button>
      <Button disabled={!manager} onClick={handleEdit}>
        {t('common:actions.edit')}
        <Icon icon={PencilSimpleIcon} />
      </Button>
    </>
  )

  return (
    <Modal
      title={t('organisation:managers.detailsModal.title')}
      headerIcon={UserIcon}
      footer={footer}
      onClose={onClose}
    >
      {isLoading || !manager ? (
        <DetailsModalSkeleton
          sections={[
            {
              labels: [t('organisation:managers.detailsModal.email')],
              title: t('organisation:managers.detailsModal.contact'),
            },
            {
              badgeRow: 0,
              labels: [
                t('organisation:managers.detailsModal.status'),
                t('organisation:managers.detailsModal.createdAt'),
                t('organisation:managers.detailsModal.locale'),
              ],
              title: t('organisation:managers.detailsModal.account'),
            },
          ]}
        />
      ) : (
        <Flex direction="column" gap={20}>
          <PersonProfile
            actions={<AccountEmailsMenu email={manager.user.email} />}
            user={manager.user}
          />

          <DetailsSection title={t('organisation:managers.detailsModal.contact')}>
            <DetailsListItem label={t('organisation:managers.detailsModal.email')}>
              <EmailLink email={manager.user.email} />
            </DetailsListItem>
          </DetailsSection>

          <DetailsSection title={t('organisation:managers.detailsModal.account')}>
            <DetailsListItem label={t('organisation:managers.detailsModal.status')}>
              <ActiveStatusBadge isActive={manager.user.isActive} />
            </DetailsListItem>
            <DetailsListItem label={t('organisation:managers.detailsModal.createdAt')}>
              {formatDateOnly(new Date(manager.createdAt))}
            </DetailsListItem>
            <DetailsListItem label={t('organisation:managers.detailsModal.locale')}>
              {manager.user.locale || EMPTY_VALUE}
            </DetailsListItem>
          </DetailsSection>
        </Flex>
      )}
    </Modal>
  )
}
